import React, { FC, useState } from "react";
import { observer } from "mobx-react";
import TickIcon from "../../../../../images/common/tick-green.svg";
import CrossIcon from "../../../../../images/products/cross-red.svg";
import {
  CheckoutPaymentPageModel,
  CustomerFacingCheckOutItemModel,
  getPurchaseItemsPriceDetails,
  PurchaseCheckOutAddOnDetails,
  PurchasePaymentModel,
} from "../models";
import {
  CoreInputModelUtil,
  CustomButton,
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  Loader,
  Merediem,
  ToastService,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { PromotionModel } from "../models/PromotionModels";
import { firstValueFrom } from "rxjs";
import PromotionService from "../services/PromotionService";
import { AuthService } from "../services";
import { CheckOutPromoCodeSelectionType } from "../enums";

type IProps = {
  paymentSectionData: PurchasePaymentModel;
  promoCodes: IOption[];
  checkoutPaymentPageModel?: CheckoutPaymentPageModel;
  handleDataChange: (field: keyof PurchasePaymentModel, value: any) => void;
  updatePaymentSectionData: (data: PurchasePaymentModel) => void;
  accountId: string;
  promoCodeSelectionType: keyof typeof CheckOutPromoCodeSelectionType;
  items: CustomerFacingCheckOutItemModel[];
  addOns: PurchaseCheckOutAddOnDetails[];
  isPurchaseLink: boolean;
};

const PurchasePromotionSection: FC<IProps> = ({
  paymentSectionData,
  handleDataChange,
  promoCodes,
  checkoutPaymentPageModel,
  updatePaymentSectionData,
  accountId,
  promoCodeSelectionType,
  items,
  addOns,
  isPurchaseLink,
}) => {
  const [loader, setLoader] = useState(false);

  const clonedItems = items.map(
    (item) =>
      new CustomerFacingCheckOutItemModel({
        ...item,
        selectedVariantIndex:
          item.selectedVariantIndex === -1 ? 0 : item.selectedVariantIndex,
      })
  );

  const summaryDetails = getPurchaseItemsPriceDetails(
    clonedItems,
    addOns,
    0,
    undefined
  );
  const totalPriceWithooutSurchargeDiscount = summaryDetails
    .map((s) => s.subTotal)
    .reduce((a, b) => a + b, 0);

  const onClickApply = async () => {
    const trimmedCode = paymentSectionData.promotionCodeValue.trim();
    if (promoCodeSelectionType === "SelectCodes") {
      const codeDetails = promoCodes.find((p) => p.label === trimmedCode);
      if (!codeDetails) {
        handleDataChange("promoText", "Your code is unavailable at this time.");
        return;
      }
    }
    try {
      await checkPromoCode(trimmedCode);
    } catch (error) {
      handleDataChange("promoText", "Your code is unavailable at this time.");
    }
  };

  const checkPromoCode = async (code: string) => {
    setLoader(true);
    try {
      const response = await firstValueFrom(
        PromotionService.checkPromoCodeByCode(code, accountId)
      );
      const clonedData = new PurchasePaymentModel({ ...paymentSectionData });
      const promoData = PromotionModel.deserializeAPIModel(response.result);
      let isValidStartDate = true;
      if (!response?.result) {
        clonedData.promoText = "This promo code is not valid.";
        clonedData.applyDisabled = true;
      } else {
        if (
          promoData.minimumPurchase &&
          promoData.minimumPurchase > 0 &&
          promoData.minimumPurchase > totalPriceWithooutSurchargeDiscount
        ) {
          clonedData.promoText = `This promo code requires a minimum purchase of $${promoData.minimumPurchase}`;
          clonedData.applyDisabled = true;
        } else {
          if (promoData.beginsDate) {
            const beginsDate = new Date(promoData.beginsDate);
            let [hours, minutes] = promoData.beginsTime.split(":").map(Number);
            if (promoData.beginsMeredium === "PM" && hours < 12) {
              hours += 12;
            } else if (promoData.beginsMeredium === "AM" && hours === 12) {
              hours = 0;
            }
            beginsDate.setHours(hours, minutes, 0, 0);

            isValidStartDate = beginsDate > new Date() ? false : true;
            if (!isValidStartDate) {
              clonedData.promoText = `This code is valid from ${UtilityMethods.formatDateInStringWithTime(
                beginsDate
              )}`;
              clonedData.applyDisabled = true;
            }
          }
          if (isValidStartDate) {
            // const isValid = promoData.endsDate
            //   ? CoreInputModelUtil.isFutureDateTime(
            //       promoData.endsDate,
            //       promoData.endsTime,
            //       promoData.endsMeredium,
            //       5
            //     )
            //   : true;
            const isValid = true;
            if (isValid) {
              clonedData.promoText = "Your code has been applied successfully.";
              clonedData.applyDisabled = true;
              clonedData.promotionRelatedData = promoData;
            } else {
              clonedData.promoText = "This promo code has expired.";
              clonedData.applyDisabled = true;
            }
          }
        }
      }
      updatePaymentSectionData(new PurchasePaymentModel({ ...clonedData }));
    } catch (error) {
      ToastService.showError("Error in getting promotion details");
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const onChangePromoValue = (value: string) => {
    handleDataChange("promotionCodeValue", value);
  };

  const renderPromotionContent = () => {
    const isSuccess = paymentSectionData.promoText?.includes("successfully");
    return (
      <>
        <FlexContainer alignItems="center" justifyContent="space-between">
          <div>
            <FlexContainer alignItems="center" spacing={10}>
              <Input
                type={"text"}
                value={paymentSectionData.promotionCodeValue}
                error={false}
                onChange={(e) => onChangePromoValue(e.target.value)}
                placeholder={"Enter Code"}
              />
              <CustomButton
                className={`promo-apply-btn ${paymentSectionData.applyDisabled ? "disabled" : "clickable"}`}
                onClick={() => onClickApply()}
                disabled={
                  paymentSectionData.applyDisabled ||
                  !paymentSectionData.promotionCodeValue
                }
              >
                Apply
              </CustomButton>
            </FlexContainer>
          </div>
        </FlexContainer>
        {paymentSectionData.promoText !== "" && (
          <FlexContainer alignItems="center" padding="10px 0px">
            <img src={isSuccess ? TickIcon : CrossIcon} alt="icon" />
            <CustomLabel fontSize={"12px"} color={"#555555"} margin="0px 10px">
              {paymentSectionData.promoText}
            </CustomLabel>
          </FlexContainer>
        )}
      </>
    );
  };

  if (
    (!isPurchaseLink &&
      (!checkoutPaymentPageModel?.isPromotionCodes ||
        promoCodeSelectionType === "ApplyCode")) ||
    (isPurchaseLink && items[0].itemType === "Donation")
  )
    return null;
  if (loader) return <>{new Loader(true).loaderComponent}</>;
  return (
    <FlexContainer fxLayout="column">
      {!paymentSectionData.isPromotionClicked ? (
        <CustomLabel
          color="#556BB8"
          //   className="not-allowed"
          onClick={() =>
            handleDataChange(
              "isPromotionClicked",
              !paymentSectionData.isPromotionClicked
            )
          }
        >
          Have a promotion code?
        </CustomLabel>
      ) : (
        renderPromotionContent()
      )}
    </FlexContainer>
  );
};

export default observer(PurchasePromotionSection);